/**
 * The three plates in "How it works".
 *
 * Drawn from the same geometry the product is drawn from — `dateToAngle`,
 * `monthArc`, `polarToXY` out of `@circle/shared` — at real dates from
 * `data/sample.ts`. The second week of March is crowded in the middle plate
 * because it is crowded in the data. Hairlines and tokens only, so a plate
 * reads the same on paper and on night.
 */
import { MONTH_NAMES, dateToAngle, daysInMonth, monthArc, polarToXY } from '@circle/shared';
import { svgEl, n } from '../../core/svg';

const W = 480;
const H = 200;

/** Sample source colours, so a plate's colours mean what they mean in the app. */
const MANUAL = 'var(--ui-brass)';
const ICS = '#6aa9d8';
const COLLECT = '#7fce9b';

/** One step stronger than the chrome's hairlines: a plate is a drawing, not a border. */
const RULE = 'var(--ui-faint)';
const HAIR = 'var(--ui-line-strong)';
const DIM = 'var(--ui-dim)';

interface Ring {
  cx: number;
  cy: number;
  r: number;
  year: number;
}

function plate(title: string, height = H): SVGSVGElement {
  return svgEl('svg', {
    class: 'plate',
    viewBox: `0 0 ${W} ${height}`,
    xmlns: 'http://www.w3.org/2000/svg',
    role: 'img',
    'aria-label': title,
    preserveAspectRatio: 'xMidYMid meet',
  });
}

function at(ring: Ring, month: number, day: number, radius = ring.r): { x: number; y: number } {
  const p = polarToXY(dateToAngle(ring.year, month, day), radius);
  return { x: ring.cx + p.x, y: ring.cy + p.y };
}

function radial(ring: Ring, angle: number, r0: number, r1: number, stroke: string): SVGLineElement {
  const inner = polarToXY(angle, r0);
  const outer = polarToXY(angle, r1);
  return svgEl('line', {
    x1: n(ring.cx + inner.x),
    y1: n(ring.cy + inner.y),
    x2: n(ring.cx + outer.x),
    y2: n(ring.cy + outer.y),
    stroke,
    'stroke-width': 1,
  });
}

/** The year circle, a tick every five days, and a longer one where a month starts. */
function ringOutline(ring: Ring): SVGGElement {
  const g = svgEl('g');
  g.append(
    svgEl('circle', {
      cx: n(ring.cx),
      cy: n(ring.cy),
      r: n(ring.r),
      fill: 'none',
      stroke: RULE,
      'stroke-width': 1,
    }),
  );
  for (let m = 1; m <= 12; m++) {
    for (let d = 1; d <= daysInMonth(ring.year, m); d += 5) {
      g.append(radial(ring, dateToAngle(ring.year, m, d), ring.r - 3.5, ring.r, HAIR));
    }
    g.append(radial(ring, monthArc(ring.year, m).start, ring.r - 8, ring.r + 3, RULE));
  }
  return g;
}

function mark(ring: Ring, month: number, day: number, colour: string, r = 3.2): SVGCircleElement {
  const p = at(ring, month, day);
  return svgEl('circle', { cx: n(p.x), cy: n(p.y), r: n(r), fill: colour });
}

function label(x: number, y: number, text: string, fill = DIM, anchor = 'start'): SVGTextElement {
  return svgEl(
    'text',
    {
      x: n(x),
      y: n(y),
      fill,
      // A plate is drawn at 480 units and shown at whatever the column is, so
      // this is a ratio to the drawing, not a pixel size.
      'font-size': 12.5,
      'text-anchor': anchor,
      'dominant-baseline': 'middle',
    },
    [text],
  );
}

function dayMonth(month: number, day: number): string {
  return `${day} ${MONTH_NAMES[month - 1]?.slice(0, 3) ?? ''}`;
}

/** A float card: rounded box, an avatar disc, a name rule and a shorter age rule. */
function card(x: number, y: number, w: number, h: number, avatar: string): SVGGElement {
  const g = svgEl('g');
  const pad = 5;
  const ar = (h - pad * 2) / 2;
  const textX = x + pad + ar * 2 + 5;
  const right = x + w - pad;
  g.append(
    svgEl('rect', {
      x: n(x),
      y: n(y),
      width: n(w),
      height: n(h),
      rx: 4,
      fill: 'var(--ui-raised)',
      stroke: HAIR,
      'stroke-width': 1,
    }),
    svgEl('circle', { cx: n(x + pad + ar), cy: n(y + h / 2), r: n(ar), fill: avatar }),
    svgEl('line', {
      x1: n(textX),
      y1: n(y + h / 2 - 3),
      x2: n(right),
      y2: n(y + h / 2 - 3),
      stroke: RULE,
      'stroke-width': 2.5,
      'stroke-linecap': 'round',
    }),
    svgEl('line', {
      x1: n(textX),
      y1: n(y + h / 2 + 3.5),
      x2: n(textX + (right - textX) * 0.42),
      y2: n(y + h / 2 + 3.5),
      stroke: HAIR,
      'stroke-width': 2,
      'stroke-linecap': 'round',
    }),
  );
  return g;
}

/** A leader: the quadratic bend the default theme draws, bent outward. */
function leader(from: { x: number; y: number }, to: { x: number; y: number }): SVGPathElement {
  return svgEl('path', {
    d: `M ${n(from.x)} ${n(from.y)} Q ${n((from.x + to.x) / 2)} ${n((from.y + to.y) / 2)} ${n(to.x)} ${n(to.y)}`,
    fill: 'none',
    stroke: HAIR,
    'stroke-width': 1,
  });
}

/**
 * Sources in — three feeds arriving at three real dates. They come in on the
 * left, where the ring's own left flank faces them, so no line ever has to
 * cross the year to reach its day.
 */
export function sourcesPlate(year = new Date().getFullYear()): SVGSVGElement {
  const svg = plate(
    'Three sources arriving at a year ring: an ICS feed, a collect link, and a birthday typed in by hand.',
  );
  const ring: Ring = { cx: 350, cy: 100, r: 78, year };
  svg.append(ringOutline(ring));

  const feeds = [
    { text: 'an ICS feed', colour: ICS, month: 11, day: 15, y: 34 },
    { text: 'a collect link', colour: COLLECT, month: 10, day: 1, y: 100 },
    { text: 'typed in by hand', colour: MANUAL, month: 8, day: 15, y: 166 },
  ];

  for (const feed of feeds) {
    const target = at(ring, feed.month, feed.day, ring.r + 1);
    const x0 = 152;
    svg.append(
      svgEl('path', {
        d: `M ${n(x0)} ${n(feed.y)} C ${n(x0 + 70)} ${n(feed.y)} ${n(target.x - 60)} ${n(target.y)} ${n(target.x)} ${n(target.y)}`,
        fill: 'none',
        stroke: feed.colour,
        'stroke-width': 1.25,
      }),
      mark(ring, feed.month, feed.day, feed.colour),
      label(144, feed.y, feed.text, feed.colour, 'end'),
    );
  }

  // The day each one lands on, set inward from its mark so the words sit
  // inside the year and never straddle the ring line.
  for (const feed of feeds) {
    const p = at(ring, feed.month, feed.day, ring.r - 26);
    svg.append(label(p.x + 4, p.y, dayMonth(feed.month, feed.day), DIM, 'start'));
  }

  return svg;
}

/** The ring itself: faces outside it, and the crowded week folded into one card. */
export function ringPlate(year = new Date().getFullYear()): SVGSVGElement {
  // Taller than the others: cards sit a card's height outside the ring, top
  // and bottom, and a plate that clips one is a plate that lies about the layout.
  const svg = plate(
    'A year ring with faces outside it, and four March birthdays folded into a single card.',
    220,
  );
  const ring: Ring = { cx: 228, cy: 110, r: 72, year };
  svg.append(ringOutline(ring));

  // The today hand, because the real ring always has one.
  const today = new Date();
  const hand = at(ring, today.getMonth() + 1, today.getDate(), ring.r);
  svg.append(
    svgEl('line', {
      x1: n(ring.cx),
      y1: n(ring.cy),
      x2: n(hand.x),
      y2: n(hand.y),
      stroke: 'var(--ui-brass)',
      'stroke-width': 1,
      opacity: 0.55,
    }),
  );

  const cw = 66;
  const ch = 19;
  const singles = [
    { month: 1, day: 4, colour: MANUAL },
    { month: 6, day: 21, colour: ICS },
    { month: 9, day: 27, colour: COLLECT },
  ];
  for (const s of singles) {
    const anchor = at(ring, s.month, s.day);
    const out = at(ring, s.month, s.day, ring.r + 26);
    const left = out.x < ring.cx;
    const x = left ? out.x - cw : out.x;
    svg.append(
      leader(anchor, { x: out.x, y: out.y }),
      card(x, out.y - ch / 2, cw, ch, s.colour),
      mark(ring, s.month, s.day, s.colour),
    );
  }

  // The four in the second week of March, exactly as `data/sample.ts` has them.
  const crowd: [number, number, string][] = [
    [3, 10, COLLECT],
    [3, 11, COLLECT],
    [3, 12, ICS],
    [3, 14, COLLECT],
  ];
  const anchor = at(ring, 3, 12);
  const out = at(ring, 3, 12, ring.r + 26);
  const clusterW = 82;
  const clusterH = 24;
  const cx0 = out.x;
  const cy0 = out.y - clusterH / 2;
  const cluster = svgEl('g');
  cluster.append(
    svgEl('rect', {
      x: n(cx0),
      y: n(cy0),
      width: n(clusterW),
      height: n(clusterH),
      rx: 4,
      fill: 'var(--ui-raised)',
      stroke: RULE,
      'stroke-width': 1,
    }),
  );
  for (let i = 0; i < 3; i++) {
    cluster.append(
      svgEl('circle', {
        cx: n(cx0 + 14 + i * 9),
        cy: n(cy0 + clusterH / 2),
        r: 6,
        fill: [COLLECT, ICS, COLLECT][i] as string,
        stroke: 'var(--ui-ground)',
        'stroke-width': 1,
      }),
    );
  }
  cluster.append(label(cx0 + 52, cy0 + clusterH / 2, '+4'));
  svg.append(leader(anchor, { x: cx0, y: out.y }), cluster);
  for (const [m, d, colour] of crowd) svg.append(mark(ring, m, d, colour, 2.6));
  svg.append(label(out.x + 6, cy0 - 10, '10–14 March'));

  return svg;
}

/** Out again — one read-only link, and a feed your phone's calendar follows. */
export function outputsPlate(year = new Date().getFullYear()): SVGSVGElement {
  const svg = plate('A year ring feeding a read-only share link and a phone calendar subscription.');
  const ring: Ring = { cx: 82, cy: 100, r: 62, year };
  svg.append(ringOutline(ring), mark(ring, 3, 12, ICS), mark(ring, 3, 14, COLLECT));

  // Two arms leaving the ring, the way a leader leaves a mark.
  for (const y of [58, 140]) {
    svg.append(
      svgEl('path', {
        d: `M ${n(ring.cx + ring.r + 4)} 100 C ${n(190)} 100 ${n(196)} ${n(y)} ${n(234)} ${n(y)}`,
        fill: 'none',
        stroke: HAIR,
        'stroke-width': 1,
      }),
    );
  }

  // A share link: a pill with a token in it, the way the share dialog shows one.
  const pillX = 240;
  const pillY = 46;
  svg.append(
    label(pillX, pillY - 12, 'anyone with the link, read-only'),
    svgEl('rect', {
      x: n(pillX),
      y: n(pillY),
      width: 206,
      height: 24,
      rx: 12,
      fill: 'var(--ui-raised)',
      stroke: RULE,
      'stroke-width': 1,
    }),
    label(pillX + 14, pillY + 12, 'circle.example/c/9f3a…'),
  );

  // A phone calendar: a header band and two rows with the ages worked out.
  const boxX = 240;
  const boxY = 114;
  const boxW = 206;
  const boxH = 54;
  svg.append(
    label(boxX, boxY - 12, 'your phone’s calendar, every year'),
    svgEl('rect', {
      x: n(boxX),
      y: n(boxY),
      width: n(boxW),
      height: n(boxH),
      rx: 5,
      fill: 'var(--ui-raised)',
      stroke: RULE,
      'stroke-width': 1,
    }),
    svgEl('line', {
      x1: n(boxX),
      y1: n(boxY + 19),
      x2: n(boxX + boxW),
      y2: n(boxY + 19),
      stroke: HAIR,
      'stroke-width': 1,
    }),
    label(boxX + 10, boxY + 10, 'March'),
    label(boxX + 12, boxY + 31, '12', DIM, 'middle'),
    label(boxX + 26, boxY + 31, 'Gustav Wren turns 77'),
    label(boxX + 12, boxY + 46, '14', DIM, 'middle'),
    label(boxX + 26, boxY + 46, 'Hanna Mbeki turns 33'),
  );

  return svg;
}
